import { AuthClient } from '@supabase/auth-js';
import { PostgrestClient } from '@supabase/postgrest-js';

/**
 * El único cliente de Supabase del navegador.
 *
 * Lleva la llave PUBLICABLE, que viaja en el bundle y está pensada para eso. La llave
 * de servicio no aparece en este proyecto del lado del navegador ni una vez: lo que la
 * necesita vive en Edge Functions.
 *
 * ---------- Por qué NO se usa `createClient` de `@supabase/supabase-js` ----------
 *
 * Medido el 2026-07-30: el paraguas cuesta 53 KB comprimidos y **24,7 de esos son código
 * que este proyecto no ejecuta nunca**. `SupabaseClient` instancia `StorageClient` y
 * `RealtimeClient` en su constructor, así que rollup no los puede podar por más que nadie
 * los use: realtime 9,79 KB, su dependencia phoenix 7,89, storage 5,73 y functions 1,32.
 *
 * Realtime no aparece en ninguna de las doce fases del plan. Storage aparece en la fase 7,
 * para los soportes de egresos, y ahí se agrega `@supabase/storage-js` donde haga falta.
 * Y las Edge Functions se llaman con `fetch` a secas, así que `functions-js` tampoco entra.
 *
 * Con `AuthClient` y `PostgrestClient` directos, el camino crítico del operario pasó de
 * 179.378 a 154.588 bytes comprimidos. El presupuesto es 184.320 y hay cuarenta pantallas
 * por escribir, así que 24 KB no son una optimización prematura: son la diferencia entre
 * tener margen y no tenerlo.
 *
 * ---------- Qué hace `createClient` que acá está replicado a mano ----------
 *
 * Poco, y está todo abajo: las dos URL derivadas de la base, la clave de almacenamiento
 * de la sesión con el mismo formato, y un `fetch` que le pone `apikey` y el
 * `Authorization` del usuario a cada petición de PostgREST. **El token se lee en cada
 * llamada y no se congela al construir**, que es exactamente lo que hace `fetchWithAuth`
 * del paraguas; congelarlo sería el defecto obvio de escribir esto a mano.
 *
 * ---------- Por qué se construye al primer uso y no al importar ----------
 *
 * El plan exportaba `const supabase = createClient(...)` en el cuerpo del módulo, y eso
 * hace que **importar** el módulo construya el cliente. Consecuencia medida el
 * 2026-07-30: `sesion.test.ts` importa `sesion.ts`, que importaba este archivo, y la
 * corrida moría entera con `Error: supabaseUrl is required.` porque en las pruebas no
 * hay variables de entorno. **Ninguna de las ocho aserciones llegaba a correr.**
 *
 * Es la adjudicación 34 otra vez, ahora en el navegador: un módulo con efecto al
 * importarlo no se puede importar desde una prueba. Ahí se resolvió partiendo el
 * archivo; acá se resuelve en la raíz, porque así **cualquier** módulo que importe el
 * cliente sigue siendo importable, y no solo este.
 */

/**
 * Las dos variables se leen con su nombre ESCRITO ENTERO, no con `import.meta.env[x]`.
 * Vite reemplaza `import.meta.env.VITE_ALGO` estáticamente al compilar; con un índice
 * calculado no lo reemplaza, y la variable llegaría indefinida en el bundle de
 * producción aunque en desarrollo funcionara.
 */
function requerido(valor: string | undefined, nombre: string): string {
  if (!valor) throw new Error(`Falta la variable de entorno ${nombre}`);
  return valor;
}

// `AuthClient` de auth-js se exporta como valor y no como tipo (es un alias de
// `GoTrueClient`), así que el tipo de una instancia hay que pedirlo con `InstanceType`.
/**
 * `schema` se expone porque **las lecturas de reporte viven en `rep` y no en `public`**:
 * `rep.v_diagnostico_jornada` es la que alimenta la hoja del cierre y la asignación del
 * faltante. Sin este método, la única forma de leerlas sería contra `public`, donde no
 * están, y la llamada daría 404 con la vista existiendo y bien construida, que es
 * exactamente el defecto que la revisión técnica encontró en las 20 llamadas a `rep`.
 * Se agregó al construir las páginas contenedoras de la fase 2, que son las primeras que
 * leen de ese esquema desde el navegador.
 */
/**
 * SE LLAMA `ClienteSupabase` Y NO `ClienteDiscopico`, y el renombre es de la tarea 23 de la
 * fase 5. Hasta el 2026-09-07 había DOS tipos con el nombre `ClienteDiscopico`, este y el de
 * `inyectable.ts`, en dos módulos distintos y con formas distintas: este es el cliente real
 * entero, y aquel es el puerto angosto que reciben las capas de llamadas de las fases 4 y 5.
 * Con el mismo nombre, leer una firma no decía cuál de los dos era, y **nadie notó durante
 * cuatro fases que el real no cumple el angosto**. Dos declaraciones con un nombre es la
 * forma exacta que la cabecera de `inyectable.ts` dice evitar.
 */
export type ClienteSupabase = {
  auth: InstanceType<typeof AuthClient>;
  rpc: PostgrestClient['rpc'];
  from: PostgrestClient['from'];
  schema: PostgrestClient['schema'];
};

let instancia: ClienteSupabase | null = null;

/**
 * El cliente, construido una sola vez.
 *
 * `detectSessionInUrl` va en falso porque no se usa ningún flujo de OAuth ni de enlace
 * mágico: la única entrada es documento y contraseña. Dejarlo en verdadero haría que la
 * aplicación intentara leer una sesión de la barra de direcciones en cada carga.
 */
export function obtenerSupabase(): ClienteSupabase {
  if (instancia !== null) return instancia;

  const base = new URL(
    requerido(import.meta.env.VITE_SUPABASE_URL as string | undefined, 'VITE_SUPABASE_URL'),
  );
  const llave = requerido(
    import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined,
    'VITE_SUPABASE_PUBLISHABLE_KEY',
  );

  const auth = new AuthClient({
    url: new URL('auth/v1', base).href,
    headers: { Authorization: `Bearer ${llave}`, apikey: llave },
    // El mismo formato que deriva `createClient`, para que una sesión guardada por
    // cualquiera de los dos la lea el otro. Cambiarlo desloguea a todo el mundo una vez.
    storageKey: `sb-${base.hostname.split('.')[0]}-auth-token`,
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false,
  });

  /**
   * El `fetch` de PostgREST, con la llave publicable y el token de quien está adentro.
   *
   * El token se pide en CADA llamada. Si se leyera una sola vez al construir, la primera
   * renovación del token dejaría al cliente hablando con una credencial vencida y las
   * escrituras empezarían a dar 401 sin motivo visible.
   */
  async function fetchConSesion(entrada: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const { data } = await auth.getSession();
    const cabeceras = new Headers(init?.headers);
    cabeceras.set('apikey', llave);
    if (!cabeceras.has('Authorization')) {
      cabeceras.set('Authorization', `Bearer ${data.session?.access_token ?? llave}`);
    }
    return fetch(entrada, { ...init, headers: cabeceras });
  }

  const rest = new PostgrestClient(new URL('rest/v1', base).href, { fetch: fetchConSesion });

  instancia = {
    auth,
    rpc: rest.rpc.bind(rest),
    from: rest.from.bind(rest),
    schema: rest.schema.bind(rest),
  };
  return instancia;
}
